import { useEffect, useState } from 'react';
import * as api from '@/sync/api';
import { usePos } from '@/store/pos';

/**
 * Every product picture the shop has, keyed by product id.
 *
 * The pictures are deliberately not in the state blob — see `Product.image` on
 * the server — so this watches the catalogue's revisions and refetches the set
 * when any of them moves. All of them, not the one that changed: a shop has
 * tens of products and one answer beats bookkeeping a per-product cache that
 * would be wrong the first time two tills edited at once.
 *
 * Empty until the first answer arrives, and empty forever if it never does. A
 * tile with no picture shows its category glyph, which is what the shop had
 * before pictures existed and is not worth an error banner.
 */
let cachedKey = '';
let cached: Record<string, string> = {};

export function useProductImages(): Record<string, string> {
  // One string standing for "which pictures the server holds". Products with no
  // picture contribute nothing, so adding the first one to a shop changes it.
  const key = usePos((s) =>
    [...s.catalog, ...s.retired]
      .filter((p) => p.imageRev)
      .map((p) => `${p.id}:${p.imageRev}`)
      .join(','),
  );
  const [images, setImages] = useState<Record<string, string>>(
    key === cachedKey ? cached : {},
  );

  useEffect(() => {
    if (key === cachedKey) {
      setImages(cached);
      return;
    }
    if (!key) {
      cachedKey = '';
      cached = {};
      setImages({});
      return;
    }

    let live = true;
    void api
      .fetchProductImages()
      .catch(() => ({}) as Record<string, string>)
      .then((found) => {
        cachedKey = key;
        cached = found;
        if (live) setImages(found);
      });
    return () => {
      live = false;
    };
  }, [key]);

  return images;
}
